import { useT } from "../lib/useT";
import { useEffect } from "react";
import { formatBytes, type OutgoingAttachment } from "../openclaw/attachments";
import type { ChatAttachment } from "../openclaw/attachments";
import { useStore } from "../state/store";
import { IconClose, IconFile } from "./icons";

/**
 * One attachment inside a message bubble.
 *
 * Inline blocks carry their bytes; artifact-backed ones carry a reference and
 * are fetched over the same authenticated socket on first render. Until the
 * bytes land the frame reserves its space, so a transcript does not jump as
 * pictures arrive.
 */
function BubbleAttachment({
  attachment,
  sessionKey,
}: {
  attachment: ChatAttachment;
  sessionKey?: string;
}) {
  const resolved = useStore((state) =>
    attachment.artifactId ? state.artifactUrls[attachment.artifactId] : undefined,
  );
  const t = useT();
  const loadArtifact = useStore((state) => state.loadArtifact);
  const needsFetch = Boolean(attachment.artifactId) && resolved === undefined;

  useEffect(() => {
    if (attachment.artifactId && needsFetch) {
      void loadArtifact(attachment.artifactId, sessionKey);
    }
  }, [attachment.artifactId, needsFetch, loadArtifact, sessionKey]);

  const source = attachment.dataUrl ?? resolved ?? attachment.url ?? "";
  const label = attachment.alt ?? attachment.fileName ?? t("attachment.image");

  if (attachment.kind !== "image") {
    return (
      <span className="attachment-file">
        <IconFile />
        <span className="attachment-file-body">
          <span className="attachment-file-name">{attachment.fileName ?? "Datei"}</span>
          {attachment.sizeBytes ? (
            <span className="attachment-file-size">{formatBytes(attachment.sizeBytes)}</span>
          ) : null}
        </span>
      </span>
    );
  }

  if (!source) {
    return (
      <span className={`attachment-pending${needsFetch || resolved === "" ? "" : " is-missing"}`}>
        {resolved === "" && !needsFetch ? t("attachment.unavailable") : t("attachment.loading")}
      </span>
    );
  }

  return (
    <a
      className="attachment-image"
      href={source}
      target="_blank"
      rel="noreferrer noopener"
      // A ratio box keeps the layout still while the picture decodes, and the
      // intrinsic width caps it so a 2x2 icon is not blown up to bubble width.
      style={
        attachment.width && attachment.height
          ? {
              aspectRatio: `${attachment.width} / ${attachment.height}`,
              width: `min(100%, ${Math.min(attachment.width, 320)}px)`,
            }
          : undefined
      }
    >
      <img src={source} alt={label} loading="lazy" />
    </a>
  );
}

export function BubbleAttachments({
  attachments,
  sessionKey,
}: {
  attachments: ChatAttachment[];
  sessionKey?: string;
}) {
  if (!attachments.length) {
    return null;
  }
  return (
    <div className={`attachment-list${attachments.length > 1 ? " is-grid" : ""}`}>
      {attachments.map((attachment) => (
        <BubbleAttachment key={attachment.id} attachment={attachment} sessionKey={sessionKey} />
      ))}
    </div>
  );
}

/** The strip of staged pictures above the composer input. */
export function DraftAttachments({
  attachments,
  onRemove,
}: {
  attachments: OutgoingAttachment[];
  onRemove: (id: string) => void;
}) {
  const t = useT();
  if (!attachments.length) {
    return null;
  }
  return (
    <div className="draft-attachments">
      {attachments.map((attachment) => (
        <div className="draft-attachment" key={attachment.id}>
          <img src={attachment.content} alt={attachment.fileName} />
          <button
            type="button"
            className="draft-attachment-remove"
            onClick={() => onRemove(attachment.id)}
            aria-label={`${t("attachment.remove")}: ${attachment.fileName}`}
            title={t("attachment.remove")}
          >
            <IconClose size={14} />
          </button>
          <span className="draft-attachment-size">{formatBytes(attachment.sizeBytes)}</span>
        </div>
      ))}
    </div>
  );
}
